import React, { createContext, useContext, useEffect, useState } from "react";
import { toast } from "sonner";

const CartContext = createContext(null);

function load(key) {
  try { return JSON.parse(localStorage.getItem(key)) || []; } catch { return []; }
}

export function CartProvider({ children }) {
  const [cart, setCart] = useState(() => load("mk_cart"));
  const [wishlist, setWishlist] = useState(() => load("mk_wishlist"));

  useEffect(() => { localStorage.setItem("mk_cart", JSON.stringify(cart)); }, [cart]);
  useEffect(() => { localStorage.setItem("mk_wishlist", JSON.stringify(wishlist)); }, [wishlist]);

  useEffect(() => {
    const token = localStorage.getItem("mk_token");
    if (!token) return;
    const t = setTimeout(() => {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cart/sync`, {
        method: "POST",
        headers: { ...(localStorage.getItem("mk_sandbox") === "1" ? { "X-Sandbox": "1" } : {}), "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ items: cart, origin_url: window.location.origin }),
      }).catch(() => {});
    }, 1500);
    return () => clearTimeout(t);
  }, [cart]);

  const addToCart = (product, qty = 1) => {
    setCart((prev) => {
      const found = prev.find((i) => i.product_id === product.id);
      if (found) {
        return prev.map((i) => i.product_id === product.id ? { ...i, quantity: i.quantity + qty } : i);
      }
      return [...prev, {
        product_id: product.id, title: product.title, price: product.price,
        image: (product.images || [])[0] || "", quantity: qty, slug: product.slug,
        vendor_name: product.vendor_name, product_type: product.product_type,
        vendor_id: product.vendor_id, vendor_house: product.vendor_house,
        stock: product.stock, document: !!product.document,
      }];
    });
    toast.success(`${product.title} added to cart`);
  };

  const updateQty = (product_id, quantity) => {
    if (quantity <= 0) return removeFromCart(product_id);
    setCart((prev) => prev.map((i) => i.product_id === product_id ? { ...i, quantity } : i));
  };

  const removeFromCart = (product_id) =>
    setCart((prev) => prev.filter((i) => i.product_id !== product_id));

  const clearCart = () => setCart([]);

  const toggleWishlist = (product) => {
    setWishlist((prev) => {
      if (prev.find((i) => i.id === product.id)) {
        toast("Removed from wishlist");
        return prev.filter((i) => i.id !== product.id);
      }
      toast.success("Saved to wishlist");
      return [...prev, product];
    });
  };

  const inWishlist = (id) => wishlist.some((i) => i.id === id);
  const cartCount = cart.reduce((s, i) => s + i.quantity, 0);
  const subtotal = cart.reduce((s, i) => s + i.price * i.quantity, 0);

  return (
    <CartContext.Provider value={{ cart, wishlist, addToCart, updateQty, removeFromCart,
      clearCart, toggleWishlist, inWishlist, cartCount, subtotal }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
